import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthentication } from '@johnlewispartnership/wtr-website-authentication/dist/context';
import graphQLService from 'services/graphql';
import { useShoppingContext } from 'contexts/ShoppingContext';
import TrolleyEventBus, {
  FINISHED_TROLLEY_UPDATE_EVENT,
  STARTED_TROLLEY_UPDATE_EVENT,
} from 'eventbus/trolley';
import TrolleyContext from './TrolleyContext';
import { FinishedTrolleyUpdateEvent, TrolleyContextValue, TrolleyState } from './types';

const getDefaultState = (loading: boolean): TrolleyState => ({
  slotChangeable: true,
  instantCheckout: 'NOT_AMENDING',
  itemTotalEstimatedCost: 0,
  totalEstimatedCost: 0,
  totalNumberOfItems: 0,
  loading,
});

const TrolleyProvider: FC<{
  children: ReactNode;
  state?: TrolleyState;
}> = ({ children, state }) => {
  const { state: authState } = useAuthentication();
  const { shoppingContext } = useShoppingContext();

  const shouldRenderLoggedInVersion =
    (authState.hasTokenSession && !authState.initialised) ||
    (authState.initialised && authState.isLoggedIn);

  const incomingTrolleyState =
    state || getDefaultState(authState.hasTokenSession && !authState.initialised);

  const [trolleyState, setTrolleyState] = useState<TrolleyState>(incomingTrolleyState);

  const shouldFetchTrolleyState =
    shouldRenderLoggedInVersion && !!shoppingContext.customerOrderId && !!authState.token;

  const onStartedTrolleyUpdateEvent = useCallback(() => {
    setTrolleyState(currentState => ({
      ...currentState,
      loading: true,
    }));
  }, []);

  const onFinishedTrolleyUpdateEvent = useCallback(
    ({ error, trolley }: FinishedTrolleyUpdateEvent) => {
      setTrolleyState(currentState => ({
        ...currentState,
        loading: false,
        ...(!error && {
          itemTotalEstimatedCost: trolley.itemTotalEstimatedCost,
          totalEstimatedCost: trolley.totalEstimatedCost,
          totalNumberOfItems: trolley.totalNumberOfItems,
          ...(currentState.trolley && {
            trolley: {
              ...currentState.trolley,
              trolleyItems: trolley.trolleyItems,
              trolleyTotals: {
                ...currentState.trolley.trolleyTotals,
                minimumSpendThresholdMet: trolley.minimumSpendThresholdMet,
              },
            },
          }),
        }),
      }));
    },
    [],
  );

  const syncTrolley = useCallback(async () => {
    setTrolleyState(currentState => ({
      ...currentState,
      loading: true,
    }));

    const trolleyResponse = await graphQLService.getTrolley({
      orderId: shoppingContext.customerOrderId,
      orderState: shoppingContext.customerOrderState,
      authHeader: authState.token!.authHeader,
    });
    if (!trolleyResponse.error) {
      setTrolleyState({
        loading: false,
        slotChangeable: trolleyResponse.slotChangeable,
        itemTotalEstimatedCost: trolleyResponse.itemTotalEstimatedCost,
        totalEstimatedCost: trolleyResponse.totalEstimatedCost,
        totalNumberOfItems: trolleyResponse.totalNumberOfItems,
        trolley: trolleyResponse.trolley,
        instantCheckout: trolleyResponse.instantCheckout,
        order: trolleyResponse.order,
      });
    } else {
      setTrolleyState(currentState => ({
        ...currentState,
        loading: false,
        error: {
          message: `Failed to fetch Trolley: ${trolleyResponse.error?.message || `Unknown error`}`,
        },
      }));
    }
  }, [authState.token, shoppingContext.customerOrderId, shoppingContext.customerOrderState]);

  useEffect(() => {
    const unsubscribeStartedTrolleyUpdateEvent = TrolleyEventBus.subscribe(
      STARTED_TROLLEY_UPDATE_EVENT,
      onStartedTrolleyUpdateEvent,
    );
    const unsubscribeFinishedTrolleyUpdateEvent = TrolleyEventBus.subscribe(
      FINISHED_TROLLEY_UPDATE_EVENT,
      (event: FinishedTrolleyUpdateEvent) => {
        if (event) {
          onFinishedTrolleyUpdateEvent(event);
        }
      },
    );

    return () => {
      unsubscribeStartedTrolleyUpdateEvent();
      unsubscribeFinishedTrolleyUpdateEvent();
    };
  }, [onFinishedTrolleyUpdateEvent, onStartedTrolleyUpdateEvent]);

  useEffect(() => {
    if (shouldFetchTrolleyState) {
      if (!trolleyState.trolley && !trolleyState.error) {
        syncTrolley();
      }
    } else {
      setTrolleyState(currentState => ({
        ...currentState,
        loading: false,
      }));
    }
  }, [shouldFetchTrolleyState, syncTrolley, trolleyState.error, trolleyState.trolley]);

  const trolleyContextValue = useMemo<TrolleyContextValue>(
    () => ({
      // TODO add any methods here that modify the state
      state: trolleyState,
    }),
    [trolleyState],
  );

  return <TrolleyContext.Provider value={trolleyContextValue}>{children}</TrolleyContext.Provider>;
};

export default TrolleyProvider;
